import React, { useEffect, useState } from "react";
import Logo from "../assets/images/logos/Mabrook-cut.svg";
import "../styles/Header.css";
import { useTranslation } from "react-i18next";
import { useLanguage, splitLanguageFromPath } from "../context/Language";
import { mabrookEmail } from "../utils/constants";
import { useScroll } from "../context/ScrollContext";
import { Link, useNavigate } from "react-router-dom";
import useClickOutsideLanguage from "../utils/useClickOutsideLanguage";
import { LuMenu, LuX } from "react-icons/lu";
import { MdArrowDropDown } from "react-icons/md";
import { trackEvent } from "../utils/analytics";
import DownloadModal from "./DownloadModal";
import LocaleLink from "./LocaleLink";
import { isEmbedded } from "../utils/embed";
import ContactModal from "./ContactModal";

const HeaderInner = ({ showDownload, setShowDownload }) => {
  const {
    language,
    changeLanguage,
    languages,
    isLanguageDropdownOpen,
    setIsLanguageDropdownOpen,
    languageDropdownRef,
    localePath,
  } = useLanguage();
  const { t } = useTranslation();
  const { sectionRefs } = useScroll();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      setVisible(
        currentScrollPos <= 100 ||
          prevScrollPos > currentScrollPos ||
          currentScrollPos < 10,
      );
      setPrevScrollPos(currentScrollPos);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos, visible]);

  const toggleDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const handleChangeLanguage = (language) => {
    toggleDropdown(false);
    changeLanguage(language);
  };

  const scrollToSection = (section) => {
    const targetSection = sectionRefs[section]?.current;
    const { rest } = splitLanguageFromPath(window.location.pathname);
    if (!targetSection || rest !== "/") {
      // Section is on the home page: go there first, keeping the language.
      navigate(`${localePath("/")}#${section}`);
    } else {
      targetSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  useClickOutsideLanguage(languageDropdownRef, () => {
    setIsLanguageDropdownOpen(false);
  });

  const SandwichMenu = () => {
    return (
      <div className="sandwich-menu">
        <div className="sandwich-header">
          <LocaleLink to="/">
            <img className="sandwich-header-logo" src={Logo} alt="Logo" />
          </LocaleLink>
          <LuX
            size={30}
            className="sandwich-menu-icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          />
        </div>
        <div className="sandwich-navbar-menu">
          <ul>
            <li
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                trackEvent({ label: "About in Resp. Header" });
                scrollToSection("about");
              }}
            >
              {t(`About`)}
            </li>
            <li
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                trackEvent({ label: "Features in Resp. Header" });
                scrollToSection("features");
              }}
            >
              {t(`Features`)}
            </li>
            <li
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                navigate(localePath("/subscribe"));
              }}
            >
              {t(`Subscribe`)}
            </li>
            <li
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                navigate(localePath("/gift"));
              }}
            >
              🎁 {t(`Gift`)}
            </li>
            <li>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  trackEvent({ label: "Contact Us in Resp. Header" });
                  setIsMenuOpen(false);
                  setShowContact(true);
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {t(`Contact Us`)}
              </a>
            </li>
            {/*<li onClick={() => setShowDownload(true)}>{t(`Get the app`)}</li>*/}
          </ul>
        </div>
        <div className="navbar-language-dropdown" ref={languageDropdownRef}>
          <div className="navbar-selected-language" onClick={toggleDropdown}>
            <p>{language.flag}</p>
            <p>{language.nativeName}</p>
            <MdArrowDropDown size={25} className="navbar-dropdownIcon" />
          </div>
          {isLanguageDropdownOpen && (
            <ul className="sandwich-navbar-dropdown-list">
              {languages.map((lang) => (
                <li
                  style={{ fontFamily: "Lato" }}
                  key={lang.value}
                  onClick={() => handleChangeLanguage(lang)}
                >
                  {lang.nativeName} {lang.flag}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  return (
    <header
      className={`header ${visible ? "visibleHeader" : "invisibleHeader"}`}
    >
      <div>
        <LocaleLink to="/">
          <img className="header-logo" src={Logo} alt="Logo" />
        </LocaleLink>{" "}
      </div>
      <nav className="nav navRegular">
        <ul>
          <li
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("about");
              trackEvent({ label: "About in Header" });
            }}
          >
            {t(`About`)}
          </li>
          <li
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("features");
              trackEvent({ label: "Features in Header" });
            }}
          >
            {t(`Features`)}
          </li>
          <li
            className="nav-subscribe-cta"
            onClick={(e) => {
              e.preventDefault();
              navigate(localePath("/subscribe"));
            }}
          >
            {t(`Subscribe`)}
          </li>
          <li
            className="nav-gift-link"
            onClick={(e) => {
              e.preventDefault();
              navigate(localePath("/gift"));
            }}
          >
            🎁 {t(`Gift`)}
          </li>
          <li>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                trackEvent({ label: "Contact Us in Header" });
                setIsMenuOpen(false);
                setShowContact(true);
              }}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {t(`Contact Us`)}
            </a>
          </li>
          {/*<li onClick={() => setShowDownload(true)}>{t(`Get the app`)}</li>*/}
        </ul>
      </nav>
      <div className="language-dropdown" ref={languageDropdownRef}>
        <div className="selected-language" onClick={toggleDropdown}>
          <span>{language.nativeName}</span>
          <span>{language.flag}</span>
          <MdArrowDropDown
            size={22}
            className={`lang-arrow ${isLanguageDropdownOpen ? "open" : ""}`}
          />
        </div>
        {isLanguageDropdownOpen && (
          <ul className="dropdown-list">
            {languages.map((lang) => (
              <li
                style={{ fontFamily: "Lato" }}
                key={lang.value}
                onClick={() => handleChangeLanguage(lang)}
              >
                {lang.nativeName} {lang.flag}
              </li>
            ))}
          </ul>
        )}
      </div>
      <nav style={{ cursor: "pointer" }} className="navResponsive">
        {isMenuOpen ? (
          <LuX size={30} onClick={() => setIsMenuOpen(false)} aria-label="Close menu" />
        ) : (
          <LuMenu size={30} onClick={() => setIsMenuOpen(true)} aria-label="Open menu" />
        )}
      </nav>
      {isMenuOpen && <SandwichMenu />}
      <ContactModal open={showContact} onClose={() => setShowContact(false)} />
    </header>
  );
};

const Header = (props) => (isEmbedded() ? null : <HeaderInner {...props} />);

export default Header;
